import { useState } from "react";
import { Check, ChevronDown, Copy, Globe2, Mail, MapPin, Phone } from "lucide-react";

const networkName = {
  instagram: "Instagram", telegram: "Telegram", vk: "ВКонтакте",
  max: "MAX", facebook: "Facebook", tiktok: "TikTok", youtube: "YouTube",
  threads: "Threads", pinterest: "Pinterest", website: "Сайт", other: "Ссылка",
};

function safeUrl(href) {
  try {
    const url = new URL(href);
    return url.protocol === "https:" && !url.username && !url.password ? url.href : null;
  } catch {
    return null;
  }
}


function ContactDetail({ label, value, type }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const Icon = type === "email" ? Mail : Phone;

  async function copyContact() {
    try {
      if (!navigator.clipboard?.writeText) return;
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // The visible value remains selectable when clipboard access is blocked.
      setCopied(false);
    }
  }

  return (
    <div className="min-w-0" data-testid={"pm-contact-" + type}>
      <button type="button" aria-expanded={expanded}
        onClick={() => { setExpanded((current) => !current); setCopied(false); }}
        className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-[#826751]/25 px-4 text-sm text-[#4A382B] hover:bg-[#F4EADD]"
        data-testid={"pm-contact-reveal-" + type}>
        <Icon size={15} aria-hidden="true"/> {label}
      </button>
      {expanded && <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-[#F8F2EA] p-3"
        data-testid={"pm-contact-detail-" + type}>
        <span className="min-w-0 select-text break-all text-sm text-[#4A382B]">{value}</span>
        <button type="button" onClick={copyContact}
          className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-[#826751]/25 bg-white px-3 text-sm hover:bg-[#F4EADD]"
          aria-label={"Скопировать " + label.toLowerCase()}>
          {copied ? <Check size={15} aria-hidden="true"/> : <Copy size={15} aria-hidden="true"/>}
          {copied ? "Скопировано" : "Копировать"}
        </button>
      </div>}
    </div>
  );
}

/**
 * One professional page, two access modes.
 * Owner data must be supplied by the authenticated owner-only cards endpoint.
 * Public data must be supplied ONLY by the paid, consented public profile API.
 * Image rendering is injected to preserve these separate media authorization paths.
 * No publication, checkout, review or private contact side effect is performed here.
 */
export default function ProfessionalProfileView({
  profile,
  renderPortrait,
  renderWork,
  contactActions = null,
  shareAction = null,
  reviews = null,
  offers = null,
  ownerPreview = false,
  onEditFaq = null,
}) {
  const [showAllWorks, setShowAllWorks] = useState(false);
  const works = profile.portfolio_media_ids || [];
  const links = (profile.social_links || [])
    .filter((link) => link.visible === true && safeUrl(link.url));
  const website = profile.public_website && safeUrl(profile.public_website);
  const hasContacts = Boolean(profile.public_email || profile.public_phone || website || links.length);
  const hasInfo = Boolean(profile.description);
  const faq = profile.professional_qa || [];
  const hasServices = (profile.services || []).length > 0;
  const aboutTitle = profile.entity_type === "business" ? "О компании" : "Обо мне";
  return (
    <article className="mt-4 grid gap-5 rounded-[24px] border border-[#826751]/20 bg-white/85 p-4 shadow-sm sm:p-6 lg:grid-cols-[270px_minmax(0,1fr)] lg:gap-8"
      data-testid="pm-public-compact-layout" data-profile-mode={ownerPreview ? "owner" : "public"}>
      <aside className="min-w-0 space-y-3 lg:sticky lg:top-5 lg:self-start" aria-label="Специалист и связь">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h1 className="min-w-0 break-words font-serif text-2xl sm:text-3xl" data-testid="pm-public-profile-name">
            {profile.public_name}
          </h1>
          {shareAction}
        </div>
        {profile.specializations?.length > 0 && (
          <p className="text-sm font-medium" data-testid="pm-public-profile-specializations">
            {profile.specializations.join(" · ")}
          </p>
        )}
        {[profile.city, profile.country].filter(Boolean).length > 0 && (
          <p className="inline-flex items-center gap-2 text-sm text-[#755F4E]">
            <MapPin size={16} aria-hidden="true"/>
            {[profile.city, profile.country].filter(Boolean).join(", ")}
          </p>
        )}
        {profile.portrait_media_id && (
          <div className="w-full max-w-[180px] overflow-hidden rounded-2xl" data-testid="pm-public-portrait">
            {renderPortrait(profile.portrait_media_id, profile)}
          </div>
        )}
        {contactActions}
      </aside>
      <div className="min-w-0" data-testid="pm-public-profile-content">
        {(hasInfo || works.length > 0 || reviews || ownerPreview || faq.length > 0 || hasContacts || hasServices) && (
          <nav className="mb-4 flex gap-4 overflow-x-auto border-b border-[#826751]/20 pb-3 text-sm"
            aria-label="Разделы профессиональной страницы" data-testid="pm-profile-section-nav">
            {hasInfo && <a href="#pm-about" className="shrink-0 hover:underline">{aboutTitle}</a>}
            {works.length > 0 && <a href="#pm-works" className="shrink-0 hover:underline">Фотографии</a>}
            {reviews && <a href="#pm-reviews" className="shrink-0 hover:underline">Отзывы</a>}
            {(faq.length > 0 || ownerPreview) && <a href="#pm-faq" className="shrink-0 hover:underline">Вопросы и ответы</a>}
            {hasContacts && <a href="#pm-links" className="shrink-0 hover:underline">Контакты</a>}
            {hasServices && <a href="#pm-services" className="shrink-0 hover:underline">Услуги</a>}
          </nav>
        )}
        {hasInfo && (
          <section id="pm-about" className="scroll-mt-5">
            <h2 className="font-serif text-xl">{aboutTitle}</h2>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-6">{profile.description}</p>
          </section>
        )}
        {works.length > 0 && (
          <section id="pm-works" className="mt-5 scroll-mt-5 border-t border-[#826751]/15 pt-4"
            aria-label="Фотографии" data-testid="pm-public-portfolio">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-serif text-xl">Фотографии</h2>
              {works.length > 6 && !showAllWorks && (
                <button type="button" onClick={() => setShowAllWorks(true)}
                  className="min-h-11 text-sm text-[#705C4D] underline underline-offset-4"
                  data-testid="pm-public-show-all-works">
                  Смотреть все фотографии ({works.length})
                </button>
              )}
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 sm:gap-3" data-testid="professional-portfolio-gallery">
              {(showAllWorks ? works : works.slice(0, 6)).map((id, index) => (
                <figure key={id} className="min-w-0">
                  {renderWork(id, index, profile.portfolio_captions?.[id] || "")}
                  {profile.portfolio_captions?.[id] && (
                    <figcaption className="mt-2 break-words text-xs text-[#665344]">
                      {profile.portfolio_captions[id]}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
            {showAllWorks && works.length > 6 && (
              <button type="button" onClick={() => setShowAllWorks(false)}
                className="mt-3 min-h-11 text-sm text-[#705C4D] underline underline-offset-4">
                Свернуть галерею
              </button>
            )}
          </section>
        )}
        {offers}
        {reviews && <div id="pm-reviews" className="scroll-mt-5">{reviews}</div>}
        {(faq.length > 0 || ownerPreview) && (
          <section id="pm-faq" className="mt-5 scroll-mt-5 border-t border-[#826751]/15 pt-4"
            aria-label="Вопросы и ответы" data-testid="pm-public-own-qa">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-serif text-xl">Вопросы и ответы</h2>
              {ownerPreview && onEditFaq && <button type="button" onClick={onEditFaq}
                className="min-h-11 rounded-xl border border-[#826751]/25 px-3 text-sm text-[#4A382B] hover:bg-[#F4EADD]"
                data-testid="pm-owner-faq-edit">{faq.length ? "Редактировать вопросы" : "Добавить вопрос и ответ"}</button>}
            </div>
            {ownerPreview && faq.length === 0 && <p className="mt-2 text-sm text-[#806B5C]" data-testid="pm-owner-faq-empty">
              Пока вопросов нет. Добавь свои вопросы и ответы — они появятся на профессиональной странице.
            </p>}
            {faq.length > 0 && <div className="mt-3 divide-y divide-[#826751]/15">
              {faq.map(({question, answer}, index) => (
                <details key={index} className="group py-2">
                  <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 text-sm font-medium text-[#4A382B]">
                    {question}
                    <ChevronDown className="h-4 w-4 shrink-0 text-[#715B4A] transition-transform group-open:rotate-180" aria-hidden="true"/>
                  </summary>
                  <p className="pb-3 whitespace-pre-wrap text-sm leading-6 text-[#705D4E]">{answer}</p>
                </details>
              ))}
            </div>}
          </section>
        )}
        {hasContacts && (
          <section id="pm-links" className="mt-5 scroll-mt-5 border-t border-[#826751]/15 pt-4"
            aria-label="Контакты и соцсети">
            <h2 className="font-serif text-xl">Контакты</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {profile.public_email && (
                <ContactDetail type="email" label="Почта" value={profile.public_email}/>
              )}
              {profile.public_phone && (
                <ContactDetail type="phone" label="Телефон" value={profile.public_phone}/>
              )}
              {website && (
                <a href={website} target="_blank" rel="noopener noreferrer"
                  className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-[#826751]/25 px-4 text-sm">
                  <Globe2 size={15} aria-hidden="true"/> Сайт
                </a>
              )}
              {links.map((link, index) => (
                <a key={link.platform + index} href={safeUrl(link.url)} target="_blank" rel="noopener noreferrer"
                  className="inline-flex min-h-11 items-center rounded-xl border border-[#826751]/25 px-4 text-sm">
                  {networkName[link.platform] || "Ссылка"}
                </a>
              ))}
            </div>
          </section>
        )}
        {hasServices && (
          <section id="pm-services" className="mt-5 scroll-mt-5 border-t border-[#826751]/15 pt-4">
            <h2 className="font-serif text-xl">Деятельность и услуги</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {profile.services.map((service, index) => (
                <span key={index} className="rounded-full bg-[#F4EADD] px-3 py-2 text-xs">{service}</span>
              ))}
            </div>
          </section>
        )}
      </div>
    </article>
  );
}
